import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Logo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Star, ArrowLeft, Factory, Store, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

export default function Pricing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [seg, setSeg] = useState("manufacturer");
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);
  const list = plans.filter((p) => p.segment === seg).sort((a, b) => a.order - b.order);

  function cta(p) {
    if (p.contact) { setContact(p); return; }
    const role = seg;
    const mode = p.price ? "register" : "register";
    nav(`/auth?role=${role}&mode=${mode}`);
  }

  async function submitContact() {
    if (!form.name || !form.company || !form.email || !form.phone) { toast.error("Please fill all required fields"); return; }
    setBusy(true);
    try {
      await api.post("/sales/contact", { ...form, segment: seg, plan_id: contact?.id });
      toast.success("Thanks! Our team will reach out within one business day.");
      setContact(null); setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5">
        <button data-testid="pricing-home" onClick={() => nav("/")} className="flex items-center gap-1 text-sm text-forest/70 hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> <Logo className="scale-90 origin-left" />
        </button>
        <Button data-testid="pricing-login" variant="ghost" className="text-forest hover:bg-psage" onClick={() => nav("/auth")}>Sign in</Button>
      </header>

      <section className="mx-auto max-w-3xl px-5 pt-10 text-center">
        <span className="overline inline-flex items-center gap-1.5 rounded-full bg-forest px-3 py-1 text-lime"><Sparkles className="h-3 w-3" /> Commerce Operating System</span>
        <h1 className="mt-5 font-heading text-4xl font-semibold tracking-tight text-forest sm:text-5xl">Pricing that scales with your distribution.</h1>
        <p className="mt-4 text-base text-ink/60">Enterprise-grade infrastructure for brands and retailers. Start lean, upgrade as you grow to 10,000+ partners.</p>
      </section>

      <div className="mx-auto mt-8 flex max-w-xs items-center gap-1 rounded-full border border-border bg-sgrey p-1">
        {[["manufacturer", "Manufacturers", Factory], ["retailer", "Retailers", Store]].map(([v, label, Icon]) => (
          <button key={v} data-testid={`seg-${v}`} onClick={() => setSeg(v)}
            className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors",
              seg === v ? "bg-forest text-white" : "text-ink/60 hover:text-forest")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <section className={cn("mx-auto grid max-w-7xl gap-6 px-5 py-12", list.length >= 3 ? "lg:grid-cols-3" : "mx-auto max-w-3xl md:grid-cols-2")}>
        {list.map((p, i) => (
          <motion.div key={p.id} data-testid={`pricing-card-${p.id}`}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
            className={cn("relative flex flex-col rounded-2xl border bg-white p-7 transition-transform hover:-translate-y-1",
              p.recommended ? "border-2 border-forest shadow-[0_20px_60px_-20px_rgba(8,71,52,0.45)]" : "border-border shadow-sm")}>
            {p.recommended && <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-lime px-3 py-1 text-xs font-bold text-ink"><Star className="h-3 w-3" /> RECOMMENDED</span>}
            <p className="font-heading text-lg font-semibold text-forest">{p.name}</p>
            <p className="mt-1 h-9 text-sm text-ink/50">{p.target}</p>
            <div className="mt-3 flex items-end gap-1">
              <span className="num text-4xl font-bold text-forest">{p.price === null ? "Custom" : p.price === 0 ? "₹0" : money(p.price).replace(".00", "")}</span>
              {p.price ? <span className="mb-1 text-sm text-ink/40">/month</span> : null}
            </div>
            {p.transaction_fee_pct != null && <p className="mt-1 text-xs text-olive">+ {p.transaction_fee_pct}% platform commission per order</p>}
            <Button data-testid={`pricing-cta-${p.id}`} onClick={() => cta(p)}
              className={cn("mt-5 w-full font-semibold", p.recommended ? "bg-lime text-ink hover:bg-lime/90" : "bg-forest text-white hover:bg-forest/90")}>
              {p.cta}
            </Button>
            <ul className="mt-6 space-y-2.5 text-sm">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-ink/70"><Check className="mt-0.5 h-4 w-4 shrink-0 text-olive" /> {f}</li>)}
            </ul>
            {p.limits && Object.keys(p.limits).length > 0 && (
              <div className="mt-5 border-t border-border pt-4 text-xs text-ink/50">
                <p className="mb-1 font-semibold uppercase tracking-wide text-ink/40">Limits</p>
                {p.limits.products !== undefined && <div>{p.limits.products === null ? "Unlimited products" : `${p.limits.products} products`}</div>}
                {p.limits.team !== undefined && <div>{p.limits.team === null ? "Unlimited team members" : `${p.limits.team} team member${p.limits.team > 1 ? "s" : ""}`}</div>}
                {p.limits.retailer_connections !== undefined && <div>{p.limits.retailer_connections === null ? "Unlimited retailer connections" : `${p.limits.retailer_connections} retailer connections`}</div>}
              </div>
            )}
          </motion.div>
        ))}
      </section>

      <Dialog open={!!contact} onOpenChange={(v) => !v && setContact(null)}>
        <DialogContent data-testid="sales-modal">
          <DialogHeader><DialogTitle>Talk to VIPER OWL Sales · {contact?.name}</DialogTitle>
            <DialogDescription>Tell us about your business and we'll craft a custom plan for you.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full name *</Label><Input data-testid="sales-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Company *</Label><Input data-testid="sales-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Work email *</Label><Input data-testid="sales-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Phone *</Label><Input data-testid="sales-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">How can we help?</Label>
              <textarea data-testid="sales-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-white p-2.5 text-sm" rows={3} /></div>
          </div>
          <DialogFooter><Button data-testid="sales-submit" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={submitContact}>Request a call</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="mx-auto flex max-w-7xl items-center justify-between px-5 py-10 text-sm text-ink/40">
        <Logo /><span>© {new Date().getFullYear()} VIPER OWL</span>
      </footer>
    </div>
  );
}
